import { ReactElement, useState } from 'react';
import { IPriceSchemesModal } from './types';
import styles from './PriceSchemesModal.module.scss';
import SelectSchemeTypeStep from './Steps/SelectSchemeTypeStep';
import CreateSchemeStep from './Steps/CreateSchemeStep';
import DuplicateSchemeStep from './Steps/DuplicateSchemeStep';
import { createPriceScheme } from 'src/api/createPriceScheme/createPriceScheme';
import { StatusBar, SnackbarStatus, Typography } from '@weareopenr/component-library';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import { duplicatePriceScheme } from 'src/api/duplicatePriceScheme/duplicatePriceScheme';
import { FieldValues } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { ImportSchemeStep } from './Steps/ImportSchemeStep/ImportSchemeStep';
import { IPriceScheme } from '../../types/price.types';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';

type TCreateSchemePayload = {
  type: 'create';
  values: {
    name: string;
  };
};

type TDuplicateSchemePayload = {
  type: 'duplicate';
  values: {
    name: string;
    schemeId: string;
  };
};

type TSchemePayload = TCreateSchemePayload | TDuplicateSchemePayload;

enum Steps {
  type = 0,
  create = 1,
  duplicate = 2,
  import = 3,
}

const stepToSubtitle = {
  [Steps.type]: 'Create a new price scheme from one of the below methods:',
  [Steps.create]: 'Creating a new empty price scheme',
  [Steps.duplicate]: 'Choose which existing pricing scheme to duplicate',
  [Steps.import]: 'Import a pricing CSV into your pricing schemes',
};

const getDefaultStep = (priceSchemeId: string, priceSchemes: IPriceScheme[]) => {
  if (!priceSchemeId && priceSchemes.length === 0) {
    return Steps.create;
  } else if (priceSchemeId) {
    return Steps.import;
  }
  return Steps.type;
};

const PriceSchemesWizard = ({
  priceSchemes = [],
  closeModal,
  priceSchemeId,
}: Omit<IPriceSchemesModal, 'open'>): ReactElement => {
  const existingPriceSchemes = priceSchemes.length > 0;
  const queryClient = useQueryClient();
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const { trackEvent, captureException } = useUserTracking();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [step, setStep] = useState(getDefaultStep(priceSchemeId, priceSchemes));

  const submitPriceScheme = async ({ type, values }: TSchemePayload) => {
    clearError();
    setIsSubmitting(true);
    try {
      type === 'create'
        ? await createPriceScheme(values)
        : await duplicatePriceScheme(values.schemeId, { name: values.name });

      await queryClient.invalidateQueries({ queryKey: [QueryKeys.priceSchemes] });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.notifications] });

      trackEvent(type === 'create' ? 'price_scheme_created' : 'price_scheme_duplicated', {
        schemeName: values.name,
      });

      showNotification({
        status: SnackbarStatus.SUCCESS,
        message: 'Price Scheme Created',
      });
      return closeModal();
    } catch (error) {
      setIsSubmitting(false);
      captureException(error, { tags: { action: `price_scheme_${type}` } });
      return showNotification({
        status: SnackbarStatus.ERROR,
        message: 'Failed to create price scheme',
      });
    }
  };

  const priceSchemeCreate = (name: string) => submitPriceScheme({ type: 'create', values: { name } });
  const priceSchemeDuplicate = (values: TDuplicateSchemePayload['values']) =>
    submitPriceScheme({ type: 'duplicate', values });

  const onCancelAction = () => (existingPriceSchemes ? setStep(Steps.type) : closeModal());
  const selectNextStep = (v: FieldValues['scheme']) => {
    if (v === 'import') {
      setStep(Steps.import);
    } else if (v === 'duplicate') {
      setStep(Steps.duplicate);
    } else {
      setStep(Steps.create);
    }
  };

  const cancelText = existingPriceSchemes ? 'Back' : 'Cancel';
  return (
    <section className={styles['wizard-container']}>
      <div>
        <Typography size="xxlarge">{step === Steps.import ? 'Import Pricing' : 'Create a New Price Scheme'}</Typography>
        <Typography size="medium" weight="regular">
          {stepToSubtitle[step]}
        </Typography>
      </div>
      {step === Steps.type && <SelectSchemeTypeStep onSubmit={selectNextStep} onCancel={closeModal} />}
      {step === Steps.create && (
        <CreateSchemeStep
          cancelText={cancelText}
          onCancel={onCancelAction}
          onSubmit={priceSchemeCreate}
          submitting={isSubmitting}
        />
      )}
      {step === Steps.duplicate && (
        <DuplicateSchemeStep
          priceSchemes={priceSchemes}
          cancelText={cancelText}
          onCancel={onCancelAction}
          submitting={isSubmitting}
          onSubmit={priceSchemeDuplicate}
        />
      )}
      {step === Steps.import && (
        <ImportSchemeStep onBack={onCancelAction} onClose={closeModal} priceSchemeId={priceSchemeId} />
      )}
      <div className={styles['error-banner']}>
        {errorMessage && <StatusBar message={errorMessage} variant="error" />}
      </div>
    </section>
  );
};

export default PriceSchemesWizard;
